import React from 'react';
import { ImprovementItem } from '../types';
import { useTranslation } from '../../../i18n';

interface ImprovementTipsCardProps {
  tips?: (string | ImprovementItem)[];
}

export const ImprovementTipsCard: React.FC<ImprovementTipsCardProps> = ({ tips = [] }) => {
  const { t } = useTranslation();
  const seenKeys = new Set<string>();
  const itemsToDisplay = tips.length > 0
    ? tips
        .map((t, idx) => {
          if (typeof t === 'string') {
            return { title: `Improvement #${idx + 1}`, description: t, category: 'COMMUNICATION', recommendation: undefined };
          }
          return {
            title: t.title || `Improvement #${idx + 1}`,
            description: t.description || '',
            recommendation: t.coachSuggestion,
            category: t.category || 'COMMUNICATION',
          };
        })
        .filter((item) => {
          const normKey = `${(item.title || '').trim().toLowerCase()}|${(item.description || '').trim().toLowerCase().slice(0, 50)}`;
          if (seenKeys.has(normKey)) return false;
          seenKeys.add(normKey);
          return true;
        })
    : [];

  return (
    <div
      className="scorecard-card"
      style={{
        background: 'var(--white)',
        border: '1px solid var(--line)',
        borderRadius: '14px',
        padding: 'clamp(14px, 3vw, 22px) clamp(12px, 3vw, 24px)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
        display: 'grid',
        gap: '16px',
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* Header with SVG Icon */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '7px',
              background: '#FFF7ED',
              color: '#EA580C',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2V16h5v-.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3z" />
            </svg>
          </span>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: '15.5px', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {t('scorecard.improvement_tips_title', undefined, 'Improvement tips')}
          </h2>
        </div>

        <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
          {t('scorecard.most_impact_first', undefined, 'Most impact first')}
        </span>
      </div>

      {/* Numbered Cards */}
      {itemsToDisplay.length === 0 && (
        <div style={{ padding: '16px', background: '#F8FAFC', borderRadius: '10px', border: '1px dashed var(--line)', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)', fontStyle: 'italic' }}>
            {t('scorecard.improvement_tips_empty', undefined, 'Improvement tips will appear here once the AI evaluation is complete.')}
          </p>
        </div>
      )}
      <div style={{ display: 'grid', gap: '14px' }}>
        {itemsToDisplay.map((tip, idx) => (
          <div
            key={idx}
            style={{
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              padding: '16px 18px',
              background: '#FFFFFF',
              display: 'grid',
              gap: '10px',
            }}
          >
            {/* Header with Number & Title */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <span
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '6px',
                  background: '#0B1E3F',
                  color: '#FFFFFF',
                  fontSize: '12px',
                  fontWeight: 800,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              >
                {idx + 1}
              </span>

              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--navy)', display: 'block' }}>
                  {tip.title}
                </span>
                <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: 'var(--muted)', lineHeight: 1.45 }}>
                  {tip.description}
                </p>
              </div>
            </div>

            {/* Actionable Recommendation Quote Box */}
            {tip.recommendation && (
              <div
                style={{
                  background: '#F5F3FF',
                  borderLeft: '3px solid #6366F1',
                  borderRadius: '6px',
                  padding: '10px 14px',
                  fontSize: '12px',
                  color: '#4338CA',
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ color: '#4338CA', display: 'inline' }}>
                  💡 {t('scorecard.actionable_recommendation_label', undefined, 'Actionable Recommendation:')}{' '}
                </strong>
                <span>{tip.recommendation}</span>
              </div>
            )}

            {/* Category Tag */}
            {tip.category && (
              <div>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em',
                    background: '#F1F5F9',
                    color: '#64748B',
                    padding: '3px 8px',
                    borderRadius: '4px',
                  }}
                >
                  {tip.category}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
